import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Card, Empty, Spin } from 'antd';
import { getRecruiterCompanyAgreementByCompany } from '../../api/recruiter';
import RecruiterTermsFeeSection from '../recruiter/RecruiterTermsFeeSection';
import { CompanyAgreement } from '../../types';

const StyledCard = styled(Card)`
  margin-top: 1rem;
  margin-bottom: 1rem;
  border-radius: ${({ theme }) => theme.radius};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
    padding: 0 24px;
  }
  
  .ant-card-head-title {
    font-weight: 700;
    font-size: 16px;
  }
  
  .ant-card-body {
    padding: 24px;
  }
`;

const EmptyContainer = styled.div`
  padding: 2rem;
  text-align: center;
`;

interface CompanyAgreementSectionProps {
    companyId: string;
}

const CompanyAgreementSection: React.FC<CompanyAgreementSectionProps> = ({ companyId }) => {
    const [loading, setLoading] = useState(true);
    const [agreement, setAgreement] = useState<CompanyAgreement | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchAgreement = async () => {
            try {
                setLoading(true);
                const response = await getRecruiterCompanyAgreementByCompany(companyId);
                let agreements: CompanyAgreement[] = [];
                if (Array.isArray(response.data)) {
                    agreements = response.data;
                } else if (response.data && Array.isArray(response.data.results)) {
                    agreements = response.data.results;
                }
                const matchingAgreement = agreements.find((a: CompanyAgreement) => a.company === companyId);
                if (matchingAgreement) {
                    setAgreement(matchingAgreement);
                    setError(null);
                } else {
                    setAgreement(null);
                    setError('No active agreement with this client.');
                }
            } catch (err) {
                console.error('Error fetching agreement:', err);
                setAgreement(null);
                setError('Failed to load agreement details.');
            } finally {
                setLoading(false);
            }
        };

        if (companyId) {
            fetchAgreement();
        }
    }, [companyId]);

    if (loading) {
        return (
            <StyledCard title="Agreement with this client">
                <Spin />
            </StyledCard>
        );
    }

    if (error || !agreement) {
        return (
            <StyledCard title="Agreement with this client">
                <EmptyContainer>
                    <Empty description={error || "No agreement information available"} />
                </EmptyContainer>
            </StyledCard>
        );
    }

    return (
        <StyledCard
            title="Agreement with this client"
        >
            <RecruiterTermsFeeSection
                agreementProvider={{
                    recruiter_company_agreement: agreement,
                    fee_currency: agreement.fee_currency,
                }}
            />
        </StyledCard>
    );
};

export default CompanyAgreementSection; 